/**
 * Resuming a video upload after the tab closed (VIDEO_PLAN V5). The server
 * keeps the upload open for 48 h with the parts R2 already has; the browser
 * lost the File, so the person picks it again and we must be sure it's the
 * same one before sending the rest.
 *
 * The server knows only the declared size. This device also remembers, per
 * upload token, the file's name and a SHA-256 of its first MiB (localStorage,
 * a per-device convenience: another device, or cleared storage, falls back to
 * the size alone). Never `lastModified`: Android's browser reports the moment
 * of picking, not the file's date (VIDEO_PLAN §12).
 */

const STORAGE_KEY = 'lyf.videoUploads.v1';
const FINGERPRINT_BYTES = 1024 * 1024;

function readAll() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(all) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch { /* storage full or blocked: resume falls back to the size check */ }
}

/** SHA-256 hex of the file's first MiB, or null where SubtleCrypto is missing. */
export async function fileFingerprint(file) {
  try {
    if (typeof crypto === 'undefined' || !crypto.subtle || typeof file?.slice !== 'function') return null;
    const bytes = await file.slice(0, FINGERPRINT_BYTES).arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

/**
 * Remember which file an upload token belongs to (on this device), and where
 * it was started (`source`, e.g. 'composer:<tripId>'), which a paused row
 * gets back after the tab closed.
 */
export async function rememberUpload(token, file, { source = null } = {}) {
  if (!token || !file) return;
  const fingerprint = await fileFingerprint(file);
  const all = readAll();
  // Merged, not replaced: the hash above takes a moment, and the heartbeat
  // (touchUpload) may have written this tab's id meanwhile — losing it makes a
  // reload read its own upload as another tab's.
  all[token] = { ...(all[token] || {}), name: file.name || null, size: file.size, fingerprint, source, savedAt: Date.now(), activeAt: Date.now() };
  // Uploads expire after 48 h; anything older is noise.
  const cutoff = Date.now() - 3 * 24 * 3600 * 1000;
  for (const [key, value] of Object.entries(all)) if (!value?.savedAt || value.savedAt < cutoff) delete all[key];
  writeAll(all);
}

export function rememberedUpload(token) {
  return readAll()[token] || null;
}

/**
 * This tab is sending this upload: a heartbeat another tab in the same
 * browser reads (isLikelyActive) before offering to cancel or re-pick it.
 */
export function touchUpload(token) {
  if (!token) return;
  const all = readAll();
  all[token] = { ...(all[token] || {}), activeAt: Date.now(), tabId: thisTabId(), savedAt: all[token]?.savedAt || Date.now() };
  writeAll(all);
}

/**
 * This tab's id, kept in sessionStorage so it survives a reload: a tab that
 * reloads mid-upload recognises its own upload (paused, pick the file again)
 * instead of reading its own heartbeat as another tab's.
 */
let fallbackTabId = null;
export function thisTabId() {
  try {
    let id = sessionStorage.getItem('lyf.videoUploads.tab');
    if (!id) {
      id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('lyf.videoUploads.tab', id);
    }
    return id;
  } catch {
    if (!fallbackTabId) fallbackTabId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    return fallbackTabId;
  }
}

// An open upload that moved this recently is someone's live upload — another
// tab, or another device — not an abandoned one. Three 8 MiB parts go up side
// by side, so on a slow phone uplink (~1 Mbps) a part lands every few
// minutes: five minutes of quiet is the line.
export const ACTIVE_WINDOW_MS = 5 * 60 * 1000;
// A tab beats every 15 s while sending, so its own last part can land a
// little after its last heartbeat (a part in flight on a slow uplink).
export const OWN_PART_SLACK_MS = 60 * 1000;

/**
 * Is this open upload (one this tab isn't sending) still being sent
 * somewhere? From the server's last part time (any device) and this
 * browser's heartbeat (another tab).
 */
export function isLikelyActive({ idleMs, remembered, tabId = thisTabId(), now = Date.now() }) {
  // This very tab sent it before a reload: its own parts are recent, so recent
  // isn't evidence — but a part that landed after this tab's last beat came
  // from somewhere else (another device took it over, V6: a Cancel here
  // would delete their upload). idle_ms is the server's own measure, so the
  // part's time is placed on this device's clock, beside activeAt.
  if (remembered?.tabId && remembered.tabId === tabId) {
    const own = Number(remembered.activeAt);
    if (!Number.isFinite(idleMs) || idleMs >= ACTIVE_WINDOW_MS || !Number.isFinite(own) || own <= 0) return false;
    return now - idleMs > own + OWN_PART_SLACK_MS;
  }
  // The server's idle time (its own clock: a wrong phone clock can't skew it)…
  if (Number.isFinite(idleMs) && idleMs < ACTIVE_WINDOW_MS) return true;
  // …or another tab of this browser beating (same device, same clock).
  const local = Number(remembered?.activeAt);
  return Number.isFinite(local) && local > 0 && now - local < ACTIVE_WINDOW_MS;
}

export function forgetUpload(token) {
  const all = readAll();
  if (!(token in all)) return;
  delete all[token];
  writeAll(all);
}

/**
 * Is `file` the one this open upload started with?
 * @param {object} upload     - the server's open upload (declared_bytes)
 * @param {object|null} remembered - rememberedUpload(token)
 * @param {string|null} fingerprint - fileFingerprint(file)
 * @returns {boolean}
 */
export function matchesPausedUpload(file, upload, remembered, fingerprint) {
  if (!file || !upload || file.size !== upload.declared_bytes) return false;
  if (!remembered) return true;
  if (remembered.size != null && remembered.size !== file.size) return false;
  if (remembered.name && file.name && remembered.name !== file.name) return false;
  if (remembered.fingerprint && fingerprint && remembered.fingerprint !== fingerprint) return false;
  return true;
}

/** How much of an open upload R2 already holds: { sentBytes, percent }. */
export function pausedProgress(upload, uploadedParts) {
  const total = upload?.declared_bytes || 0;
  const size = upload?.part_size || 0;
  if (!total || !size) return { sentBytes: 0, percent: 0 };
  const sent = (uploadedParts || []).reduce((sum, n) => sum + Math.max(0, Math.min(size, total - (n - 1) * size)), 0);
  return { sentBytes: sent, percent: Math.min(100, Math.floor((sent / total) * 100)) };
}
